import type { GoalTreeNode, Project, TaskStatus, Template } from '../api';
import type { Option } from './Select';
import { goalColorMap } from '../colors';
import { StatusBadge, PriorityMark } from './StatusBadge';
import { STATUS_LOOK, PRIORITY_LABELS, type DisplayStatus } from '../taskVocab';

/**
 * Turning the app's things into picker options.
 *
 * This file exists to delete something. Six components each carried their own `flattenGoals` with
 * the line `` `${'— '.repeat(depth)}${n.goal.title}` `` — em-dashes glued onto a title to fake
 * indentation, because a native `<option>` holds text and nothing else. The depth is now a number
 * on the option and the indentation is CSS, so the hack has one fewer reason to exist and zero
 * copies.
 */

export interface GoalOption {
  colorClass: string;
  percent: number | null;
}

/** Every goal, flat, carrying its depth and its colour. */
export function goalOptions(tree: GoalTreeNode[]): Option<GoalOption>[] {
  const colors = goalColorMap(tree);
  const out: Option<GoalOption>[] = [];
  const walk = (nodes: GoalTreeNode[], depth: number) => {
    for (const node of nodes) {
      out.push({
        value: node.goal.id,
        label: node.goal.title,
        depth,
        data: {
          colorClass: colors.get(node.goal.id) ?? '',
          percent: node.progress?.percent ?? null,
        },
      });
      walk(node.children, depth + 1);
    }
  };
  walk(tree, 0);
  return out;
}

export const renderGoalOption = (option: Option<GoalOption>) => (
  <>
    <span className={`swatch ${option.data?.colorClass ?? ''}`} />
    <span className="t">{option.label}</span>
    {option.data?.percent !== null && option.data !== undefined && (
      <span className="sub">{option.data.percent}%</span>
    )}
  </>
);

export function projectOptions(projects: Project[]): Option<Project>[] {
  return projects.map((p) => ({ value: p.id, label: p.title, data: p }));
}

export const renderProjectOption = (option: Option<Project>) => (
  <>
    <span className="t">{option.label}</span>
    {option.data && option.data.status !== 'active' && (
      <span className="sub">{option.data.status}</span>
    )}
  </>
);

/**
 * Statuses and priorities as picker options, with the badge and the mark they are drawn with
 * everywhere else.
 *
 * These were the last two native `<select>`s carrying a closed vocabulary. The owner asked for
 * "colors and icons to statuses, priorities" in the same breath as the picker replacement, and a
 * `<option>` holds text and nothing else — so the badge existed on every task row and every board
 * card while the control you *set* the value with showed the bare word.
 */
export function statusOptions(): Option<{ status: TaskStatus }>[] {
  // `STATUS_LOOK` minus `blocked`, which is derived from the task-links graph and displayed but
  // never stored — offering it here would be offering a write that cannot happen.
  return (Object.keys(STATUS_LOOK) as DisplayStatus[])
    .filter((s): s is TaskStatus => s !== 'blocked')
    .map((status) => ({ value: status, label: STATUS_LOOK[status].label, data: { status } }));
}

export const renderStatusOption = (option: Option<{ status: TaskStatus }>) => (
  <>
    {option.data && <StatusBadge status={option.data.status} compact />}
    <span className="t">{option.label}</span>
  </>
);

export function priorityOptions(): Option<{ priority: number }>[] {
  return [0, 1, 2, 3].map((priority) => ({
    value: String(priority),
    label: PRIORITY_LABELS[priority] ?? String(priority),
    data: { priority },
  }));
}

export const renderPriorityOption = (option: Option<{ priority: number }>) => (
  <>
    {option.data && option.data.priority > 0 && <PriorityMark priority={option.data.priority} />}
    <span className="t">{option.label}</span>
  </>
);

export interface TemplateOption {
  kind: 'single' | 'bundle';
  icon: string | null;
  /** "every week on Sunday", already phrased by the server. */
  recurrenceText: string | null;
  useCount: number;
  estimateMinutes: number | null;
}

/**
 * Templates as picker options.
 *
 * The last native `<select>` in the app, and the one with the most to say that a `<option>` could
 * not: a template is a single or a bundle, it may recur, it may carry an estimate, and it has been
 * used some number of times. All of that was collapsed to a name.
 *
 * The server already orders them most-recently-used first, so the list is left in the order it
 * arrives — the one you reach for is usually near the top, and re-sorting here would throw that
 * away.
 */
export function templateOptions(templates: readonly Template[]): Option<TemplateOption>[] {
  return templates.map((t) => ({
    value: t.id,
    label: t.name,
    data: {
      kind: t.kind,
      icon: t.icon,
      recurrenceText: t.recurrenceText,
      useCount: t.useCount,
      estimateMinutes: t.defaultEstimateMinutes,
    },
  }));
}

export const renderTemplateOption = (option: Option<TemplateOption>) => {
  const d = option.data;
  /*
    One line of "what is this", built from whichever facts the template actually has. An empty
    template says nothing rather than printing "single · used 0×", which is noise dressed as
    information.
  */
  const notes = [
    d?.kind === 'bundle' ? 'bundle' : null,
    d?.recurrenceText ?? null,
    d?.estimateMinutes ? `${d.estimateMinutes}m` : null,
    d && d.useCount > 0 ? `used ${d.useCount}×` : null,
  ].filter(Boolean);

  return (
    <>
      {d?.icon ? <span aria-hidden="true">{d.icon}</span> : null}
      <span className="t">{option.label}</span>
      {notes.length > 0 && <span className="sub">{notes.join(' · ')}</span>}
    </>
  );
};
